import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background:
    radial-gradient(700px 400px at 100% 20%, rgba(0,153,0,0.16), transparent 60%),
    radial-gradient(600px 400px at 0% 90%, rgba(0,153,0,0.13), transparent 60%),
    linear-gradient(180deg, var(--background-dark), var(--background-light));
  padding: 5rem 1rem 3rem;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  position: relative; z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 3rem;
  align-items: center;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Copy = styled.div` color: var(--text-color); `;

const Pill = styled.span`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(0,153,0,0.55);
  background: rgba(0,153,0,0.12);
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--primary-color); box-shadow: 0 0 12px var(--primary-color); animation: p 2s ease-in-out infinite; }
  @keyframes p { 0%,100% { opacity: 1 } 50% { opacity: .5 } }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5.5vw, 4.25rem);
  line-height: 1.02;
  margin: 0 0 1.25rem;
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 2rem;
  max-width: 620px;
  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;

const PrimaryLink = styled.a`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled(Link)`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const Chips = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 2rem;
  @media (max-width: 900px) { justify-content: center; }
`;

const Chip = styled.span`
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.04);
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
`;

const Right = styled.div`
  position: relative;
`;

const Ring = styled.div`
  position: absolute;
  inset: -30px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(0,153,0,0.25), transparent 60%);
  z-index: 0;
`;

const Main = styled.figure`
  position: relative;
  z-index: 1;
  margin: 0;
  aspect-ratio: 1;
  border-radius: 2rem;
  overflow: hidden;
  border: 1px solid rgba(0,153,0,0.35);
  background: rgba(0,0,0,0.4);
  display: grid;
  place-items: center;
  padding: 3rem;
  img { max-width: 85%; height: auto; object-fit: contain; display: block; }
`;

const Floats = styled.div`
  position: absolute;
  z-index: 2;
  right: -1rem;
  bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  @media (max-width: 900px) { display: none; }

  .float {
    background: rgba(4,12,7,0.9);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0,153,0,0.35);
    border-radius: 0.9rem;
    padding: 0.75rem 1rem;
    display: flex;
    gap: 0.6rem;
    align-items: center;
    color: var(--text-color);
    min-width: 180px;
    box-shadow: 0 15px 40px -15px rgba(0,0,0,0.7);
  }
  .icon {
    width: 32px; height: 32px;
    border-radius: 8px;
    background: rgba(0,153,0,0.15);
    color: var(--primary-color);
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 800;
  }
  strong { display: block; font-size: 0.85rem; }
  small { color: var(--text-secondary); font-size: 0.72rem; }
`;

const BannerHero: React.FC = () => (
  <Wrap>
    <Inner>
      <Copy>
        <Pill><span className="dot" /> Pro-grade USDT flashing software</Pill>
        <Title>
          Generate <span>flash USDT</span> on TRC-20, ERC-20 &amp; BEP-20 like a pro.
        </Title>
        <Lede>
          {siteConfig.brand.name} is the professional flash USDT generator for developers,
          traders and QA teams. Realistic USDT balances across 50+ wallets, up to $10M
          daily flash cap, 365-day validity, 24/7 support.
        </Lede>
        <Actions>
          <PrimaryLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Buy on WhatsApp
          </PrimaryLink>
          <GhostLink to="/pricing">View pricing plans</GhostLink>
        </Actions>
        <Chips>
          <Chip>TRC-20</Chip><Chip>ERC-20</Chip><Chip>BEP-20</Chip>
          <Chip>Trust Wallet</Chip><Chip>MetaMask</Chip><Chip>Binance Wallet</Chip>
        </Chips>
      </Copy>
      <Right>
        <Ring aria-hidden="true" />
        <Main>
          <img src="/img/hero-usdt-mark.png" alt={`${siteConfig.brand.name} — flash USDT generator with multi-chain support`} />
        </Main>
        <Floats>
          <div className="float"><span className="icon">$</span><div><strong>$10M</strong><small>Daily cap</small></div></div>
          <div className="float"><span className="icon">✓</span><div><strong>50+ wallets</strong><small>All major crypto wallets</small></div></div>
          <div className="float"><span className="icon">✦</span><div><strong>365-day</strong><small>Flash validity</small></div></div>
        </Floats>
      </Right>
    </Inner>
  </Wrap>
);

export default BannerHero;
